import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { TOOL_INDEX } from '../aidata/toolRegistry';

export interface ToolPick {
  name: string;
  args: Record<string, unknown>;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onPick: (pick: ToolPick) => void;
  plan: string;
}

type IconKey =
  | 'face'
  | 'face-premium'
  | 'product'
  | 'compare'
  | 'compat'
  | 'sun'
  | 'moon'
  | 'weather'
  | 'prefs';

interface MenuItem {
  toolName: string;
  args: Record<string, unknown>;
  label: string;
  hint: string;
  icon: IconKey;
}

interface Group {
  title: string;
  items: MenuItem[];
}

/**
 * The picker is a CURATED surface — only show actions that make sense as a
 * user-initiated click. The model still has access to all 18 tools via the
 * registry; this list intentionally excludes lookups (analyze_skin, get_*,
 * etc.) that would require typing a job_id / product_id / etc.
 */
const GROUPS: Group[] = [
  {
    title: 'Skin',
    items: [
      { toolName: 'ai_face_scan',         args: {}, icon: 'face',         label: 'New face scan',     hint: 'Quick selfie analysis' },
      { toolName: 'ai_premium_face_scan', args: {}, icon: 'face-premium', label: 'Premium face scan', hint: 'Detailed · 768px crop' },
    ],
  },
  {
    title: 'Products',
    items: [
      { toolName: 'product_solo_analysis', args: {}, icon: 'product', label: 'Scan a product',       hint: 'Barcode or photo' },
      { toolName: 'product_compare',       args: {}, icon: 'compare', label: 'Compare two products', hint: 'Side by side' },
      { toolName: 'product_compatibility', args: {}, icon: 'compat',  label: 'Will these layer?',    hint: 'Compatibility check' },
    ],
  },
  {
    title: 'Routine',
    items: [
      { toolName: 'get_routine', args: { time_of_day: 'morning' }, icon: 'sun',  label: 'Morning routine', hint: 'AM steps tailored to you' },
      { toolName: 'get_routine', args: { time_of_day: 'evening' }, icon: 'moon', label: 'Evening routine', hint: 'PM steps tailored to you' },
    ],
  },
  {
    title: 'Today',
    items: [
      { toolName: 'weather_rating', args: {}, icon: 'weather', label: 'Today’s UV & weather', hint: 'UV · AQI · pollution' },
    ],
  },
  {
    title: 'You',
    items: [
      { toolName: 'update_user_preferences', args: {}, icon: 'prefs', label: 'Edit my preferences', hint: 'Avoid-list · sensitivity · flags' },
    ],
  },
];

function ItemIcon({ name }: { name: IconKey }) {
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (name) {
    case 'face':
      return (
        <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M9 10h.01M15 10h.01M9 15c1 1 5 1 6 0" /></svg>
      );
    case 'face-premium':
      return (
        <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 7l1.4 2.7L16 10l-2 1.8.5 2.7L12 13.2 9.5 14.5 10 11.8 8 10l2.6-.3z" /></svg>
      );
    case 'product':
      return (
        <svg {...common}><rect x="6" y="3" width="12" height="18" rx="2" /><path d="M9 8h6M9 12h6" /></svg>
      );
    case 'compare':
      return (
        <svg {...common}><rect x="3" y="5" width="8" height="14" rx="1.5" /><rect x="13" y="5" width="8" height="14" rx="1.5" /><path d="M12 3v18" strokeDasharray="2 3" /></svg>
      );
    case 'compat':
      return (
        <svg {...common}><circle cx="8" cy="12" r="4" /><circle cx="16" cy="12" r="4" /></svg>
      );
    case 'sun':
      return (
        <svg {...common}><circle cx="12" cy="12" r="4" /><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4" /></svg>
      );
    case 'moon':
      return (
        <svg {...common}><path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" /></svg>
      );
    case 'weather':
      return (
        <svg {...common}><path d="M16 17H7a4 4 0 1 1 1-7.9A6 6 0 0 1 19 11" /><path d="M12 19v3M9 21v2M15 21v2" /></svg>
      );
    case 'prefs':
      return (
        <svg {...common}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06A2 2 0 0 1 3.78 16.96l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H2.5a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06A2 2 0 1 1 6.54 4.3l.06.06A1.65 1.65 0 0 0 8.42 4.7H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09c0 .67.39 1.27 1 1.51a1.65 1.65 0 0 0 1.82-.33l.06-.06A2 2 0 1 1 19.7 7.04l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.24.61.84 1 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
      );
  }
}

const ToolPickerSheet: React.FC<Props> = ({ open, onClose, onPick, plan }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  const items = useMemo(
    () =>
      GROUPS.map((g) => ({
        ...g,
        items: g.items
          .map((mi) => ({ ...mi, def: TOOL_INDEX[mi.toolName] }))
          .filter((mi) => Boolean(mi.def)),
      })),
    [],
  );

  if (!open) return null;

  const handleSelect = (item: MenuItem) => {
    onPick({ name: item.toolName, args: item.args });
  };

  return ReactDOM.createPortal(
    <div className="kv-pick-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} role="presentation">
      <div
        className="kv-pick-sheet"
        role="dialog"
        aria-label="Quick actions"
      >
        <div className="kv-pick-grabber" />
        <div className="kv-pick-header">
          <div>
            <div className="kv-pick-title">Quick actions</div>
            <div className="kv-pick-subtitle">Run a tool — Kivo handles the details.</div>
          </div>
          <button type="button" className="kv-pick-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="kv-pick-scroll">
          {items.map((g) => (
            <div key={g.title} className="kv-pick-group">
              <div className="kv-pick-group__title">{g.title}</div>
              <div className="kv-pick-group__items">
                {g.items.map((mi, i) => {
                  const locked = mi.def.requiresPlan === 'premium' && plan !== 'premium';
                  return (
                    <button
                      key={`${mi.toolName}-${i}`}
                      type="button"
                      className={`kv-pick-item${locked ? ' is-locked' : ''}`}
                      onClick={() => { if (!locked) handleSelect(mi); }}
                      disabled={locked}
                      title={locked ? 'Premium plan required' : mi.hint}
                    >
                      <span className="kv-pick-item__icon">
                        <ItemIcon name={mi.icon} />
                      </span>
                      <span className="kv-pick-item__body">
                        <span className="kv-pick-item__label">{mi.label}</span>
                        <span className="kv-pick-item__hint">{locked ? 'Premium plan' : mi.hint}</span>
                      </span>
                      {locked && <span className="kv-pick-item__lock">★</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ToolPickerSheet;
